export class StickyHeader extends HTMLElement {
  static TRANSITION = 'top 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
  static SCROLL_THRESHOLD = 200
  static UI_VISIBLE_SELECTOR = '[data-ui~="visible"]'
  static TOP_BAR_SELECTOR = '#shopify-section-topbar'
  static HIDDEN_CLASS = 'is-hidden'
  lastScrollY: number
  ticking: boolean
  isVisible: boolean
  topOffset: number
  topbarElement: HTMLElement | null
  elementHeight: number
  headerElement: HTMLElement | null
  initialTopOffset: number

  constructor() {
    super()

    // State variables
    this.lastScrollY = window.scrollY
    this.ticking = false
    this.isVisible = true

    this.style.transition = StickyHeader.TRANSITION
    this.topOffset = parseInt(window.getComputedStyle(this).top.replace('px', '')) || 0
    this.initialTopOffset = this.topOffset
    this.headerElement = this.querySelector('#header')
    this.topbarElement = document.querySelector(StickyHeader.TOP_BAR_SELECTOR)

    this.elementHeight = this.headerElement
      ? this.headerElement.getBoundingClientRect().height
      : this.getBoundingClientRect().height

    // Bind scroll handler
    this.handleScroll = this.handleScroll.bind(this)
  }

  get hiddenPosition() {
    return `-${this.elementHeight + this.initialTopOffset}px`
  }

  get visiblePosition() {
    return `${this.initialTopOffset}px`
  }

  get topbarHeight() {
    return this.topbarElement ? this.topbarElement.getBoundingClientRect().height : 0
  }

  get isTopbarVisible() {
    if (!this.topbarElement) return false
    const topbarRect = this.topbarElement.getBoundingClientRect()
    return topbarRect.bottom > 0
  }

  connectedCallback() {
    this.topOffset = parseInt(window.getComputedStyle(this).top.replace('px', '')) || 0
    this.initialTopOffset = this.topOffset
    this.headerElement = this.querySelector('#header')
    this.topbarElement = document.querySelector(StickyHeader.TOP_BAR_SELECTOR)
    this.elementHeight = this.headerElement
      ? this.headerElement.getBoundingClientRect().height
      : this.getBoundingClientRect().height

    window.addEventListener('scroll', this.handleScroll, { passive: true })

    this.style.top = this.hiddenPosition
    requestAnimationFrame(() => {
      this.style.transition = StickyHeader.TRANSITION
      this.updateHeaderPosition()
      this.classList.remove(StickyHeader.HIDDEN_CLASS)
    })
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll)
  }

  checkForVisibleUI() {
    return this.querySelector(StickyHeader.UI_VISIBLE_SELECTOR)
  }

  showHeader() {
    if (!this.isVisible) {
      this.updateHeaderPosition()
      this.isVisible = true
      this.setAttribute('data-visible', 'visible')
    } else {
      this.updateHeaderPosition()
    }
  }

  hideHeader() {
    if (this.isVisible && !this.checkForVisibleUI()) {
      this.style.top = this.hiddenPosition
      this.isVisible = false
      this.removeAttribute('data-visible')
    }
  }

  updateHeaderPosition() {
    if (this.isTopbarVisible) {
      // If topbar is visible, position header below it
      this.style.top = `${this.initialTopOffset + this.topbarHeight}px`
    } else {
      // If topbar is not visible, use normal offset
      this.style.top = this.visiblePosition
    }
  }

  handleScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        const currentScroll = window.scrollY

        // Show header if we're at the top or scrolling up
        if (currentScroll < StickyHeader.SCROLL_THRESHOLD ||
            (currentScroll < this.lastScrollY && currentScroll > StickyHeader.SCROLL_THRESHOLD)) {
          this.showHeader()
        } else if (currentScroll > this.lastScrollY && currentScroll > StickyHeader.SCROLL_THRESHOLD) {
          // Hide header if scrolling down past threshold
          this.hideHeader()
        } else if (this.isVisible) {
          // Update position in case topbar visibility changed
          this.updateHeaderPosition()
        }
        this.lastScrollY = currentScroll
        this.ticking = false
      })

      this.ticking = true
    }
  }
}
